import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(
        isLogin ? "/auth/login" : "/auth/register",
        isLogin ? { email: form.email, password: form.password } : form
      );
      setUser(data);
      toast.success(isLogin ? t("auth.welcome_back") : t("auth.account_created"));
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] mesh-bg grid-overlay flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </span>
          <span className="font-heading font-bold text-2xl text-white">Digisídlo</span>
        </Link>
        <div className="bg-[#131B2A] border border-slate-800 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white">{isLogin ? t("auth.login_title") : t("auth.register_title")}</h1>
          <p className="mt-1 text-sm text-slate-400">
            {isLogin ? t("auth.login_sub") : t("auth.register_sub")}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {!isLogin && (
              <input data-testid="auth-name-input" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("auth.name_ph")}
                className="w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 transition-colors" />
            )}
            <input data-testid="auth-email-input" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("auth.email_ph")}
              className="w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 transition-colors" />
            <input data-testid="auth-password-input" required type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={t("auth.password_ph")}
              className="w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 transition-colors" />
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="auth-submit-button" disabled={loading}
              className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors disabled:opacity-50">
              {loading ? t("auth.loading") : isLogin ? t("auth.submit_login") : t("auth.submit_register")}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-400 text-center">
            {isLogin ? (
              <>{t("auth.no_account")} <Link data-testid="auth-switch-register" to="/register" className="text-indigo-400 hover:text-indigo-300">{t("nav.register")}</Link></>
            ) : (
              <>{t("auth.have_account")} <Link data-testid="auth-switch-login" to="/login" className="text-indigo-400 hover:text-indigo-300">{t("nav.login")}</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
